/**
 * Dynamic resolution scaling (BUILD_PLAN section 4): 30 consecutive over-budget frames step the
 * render scale down 10 %, headroom steps it back up, floor 0.5. The sample is the GPU frame time
 * from timestamp queries (or the CPU render time when unavailable), never the animation-frame
 * interval, so a 30 Hz display cannot be mistaken for a slow GPU. Pure and unit-tested.
 */

export interface DrsConfig {
  /** Frame-time budget in milliseconds (16.7 at 60 fps). */
  targetMs: number;
  minScale: number;
  /** The user's render-scale slider. */
  maxScale: number;
  /** Consecutive over-budget samples before stepping down. */
  overFrames: number;
  /** Consecutive samples with predicted headroom before stepping up. */
  headroomFrames: number;
  /** Samples ignored after any change so the new scale can settle. */
  cooldownFrames: number;
}

export interface DrsState {
  scale: number;
  over: number;
  under: number;
  cooldown: number;
}

export const STEP = 0.9;

export function drsConfig(targetMs: number, maxScale: number): DrsConfig {
  return {
    targetMs,
    minScale: 0.5,
    maxScale,
    overFrames: 30,
    headroomFrames: 90,
    cooldownFrames: 30,
  };
}

export function initialDrs(maxScale: number): DrsState {
  return { scale: maxScale, over: 0, under: 0, cooldown: 0 };
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

export function drsStep(st: DrsState, sampleMs: number | null, cfg: DrsConfig): DrsState {
  const scale = Math.min(st.scale, cfg.maxScale);
  if (sampleMs === null) return { ...st, scale };
  if (st.cooldown > 0) return { scale, over: 0, under: 0, cooldown: st.cooldown - 1 };

  const over = sampleMs > cfg.targetMs ? st.over + 1 : 0;
  const up = Math.min(cfg.maxScale, round2(scale / STEP));
  // Fill-rate model: cost grows with the pixel count, so predict the cost at the next scale.
  const predictedUp = sampleMs * (up / scale) ** 2;
  const under = up > scale && predictedUp < cfg.targetMs * 0.9 ? st.under + 1 : 0;

  if (over >= cfg.overFrames) {
    return {
      scale: Math.max(cfg.minScale, round2(scale * STEP)),
      over: 0,
      under: 0,
      cooldown: cfg.cooldownFrames,
    };
  }
  if (under >= cfg.headroomFrames) {
    return { scale: up, over: 0, under: 0, cooldown: cfg.cooldownFrames };
  }
  return { scale, over, under, cooldown: 0 };
}

/** Budget derived from the fps cap: uncapped still budgets for 60 fps (BUILD_PLAN section 4). */
export function budgetMs(fpsCap: 0 | 60 | 120): number {
  return 1000 / (fpsCap === 0 ? 60 : fpsCap);
}
